import React, { useMemo } from 'react';
import { Alert, Button, Divider, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IconInfoCircle, IconFileDownload } from '@tabler/icons-react';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import ManagedPartsTable from '../ManagedPartsTable';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('managed-parts-coverage');

const SSP_CURRENT = 'SSP current';
const SSP_EXPIRED = 'SSP expired';
const SSP_NO_DATE = 'SSP, no priced date';
const SSP_NONE = 'No SSP';

// The boss's transition memos, copied into public/ so the exec team can download them.
const MEMOS = [
  {
    label: 'Managed Material — Transition & New Way of Thinking',
    file: 'ITS_Managed_Material_Transition_and_New_Way_of_Thinking_Updated_v2.docx',
  },
  {
    label: 'Managed vs Non-Managed — One-Page Summary',
    file: 'ITS_Managed_vs_Non-Managed_One-Page_Summary_Updated_v4.docx',
  },
];
const memoHref = (f) => `${process.env.PUBLIC_URL || ''}/managed-material/${f}`;

function SectionHeading({ title, subtitle }) {
  return (
    <Stack gap={4} mt="lg">
      <Text fw={800} size="xl">
        {title}
      </Text>
      {subtitle && (
        <Text size="sm" c="dimmed">
          {subtitle}
        </Text>
      )}
      <Divider mt={2} />
    </Stack>
  );
}

export default function ManagedPartsCoverage() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const coverage = useNexusQuery('initiative-managed-parts-coverage', window);
  const composition = useNexusQuery('initiative-managed-parts-composition', window);
  const catalogPricing = useNexusQuery('initiative-managed-catalog-pricing', window);
  const costShare = useNexusQuery('initiative-managed-cost-share', window);
  const channel = useNexusQuery('initiative-managed-by-channel', window);
  const velocity = useNexusQuery('initiative-managed-velocity', window);
  const stockReadiness = useNexusQuery('initiative-managed-stock-readiness', window);

  // --- Sold-revenue SSP split (existing) --------------------------------------------------------
  const rows = useMemo(
    () =>
      coverage.rows.map((r) => ({
        managed: field(r, 'ManagedStatus'),
        ssp: field(r, 'SspStatus'),
        sortOrder: num(r, 'SortOrder'),
        lines: num(r, 'Lines'),
        parts: num(r, 'Parts'),
        revenue: num(r, 'Revenue'),
        marginPct: num(r, 'GrossMarginPct'),
      })),
    [coverage.rows]
  );

  const managedBySsp = useMemo(
    () => rows.filter((r) => r.managed === 'Managed').sort((a, b) => a.sortOrder - b.sortOrder),
    [rows]
  );

  const summary = useMemo(() => {
    const managed = rows.filter((r) => r.managed === 'Managed');
    const unmanaged = rows.filter((r) => r.managed === 'Unmanaged');
    const sum = (arr, k) => arr.reduce((s, r) => s + r[k], 0);
    const managedRev = sum(managed, 'revenue');
    const totalRev = managedRev + sum(unmanaged, 'revenue');
    const current = managed.find((r) => r.ssp === SSP_CURRENT);
    const expired = managed.find((r) => r.ssp === SSP_EXPIRED);
    return {
      managedRev,
      totalRev,
      managedShare: totalRev ? (managedRev / totalRev) * 100 : 0,
      managedLines: sum(managed, 'lines'),
      current,
      expired,
      currentShare: managedRev ? ((current?.revenue ?? 0) / managedRev) * 100 : 0,
    };
  }, [rows]);

  // --- Population (composition by class) --------------------------------------------------------
  const compositionData = useMemo(
    () =>
      composition.rows.map((r) => ({
        cls: field(r, 'Class'),
        parts: num(r, 'ManagedParts'),
        priced: num(r, 'WithAnySellPrice'),
      })),
    [composition.rows]
  );

  const popTotals = useMemo(() => {
    const total = compositionData.reduce((s, d) => s + d.parts, 0);
    const ab = compositionData
      .filter((d) => d.cls === 'A' || d.cls === 'B')
      .reduce((s, d) => s + d.parts, 0);
    return { total, ab, tail: total - ab };
  }, [compositionData]);

  const reasonData = useMemo(() => {
    const ab = compositionData
      .filter((d) => d.cls === 'A' || d.cls === 'B')
      .reduce((s, d) => s + d.parts, 0);
    const repaired = compositionData
      .filter((d) => d.cls !== 'A' && d.cls !== 'B')
      .reduce((s, d) => s + d.parts, 0);
    return [
      { reason: 'Class A/B (deliberate)', parts: ab },
      { reason: 'Repaired stock on shelf (SV/OH)', parts: repaired },
    ].filter((d) => d.parts > 0);
  }, [compositionData]);

  // --- Section 2: pricing on the catalog + on-hand inventory ------------------------------------
  const pricing = useMemo(
    () =>
      catalogPricing.rows.map((r) => ({
        cls: field(r, 'Class'),
        managed: num(r, 'ManagedParts'),
        current: num(r, 'CurrentSSP'),
        expired: num(r, 'ExpiredSSP'),
        setNoDate: num(r, 'SetNoDate'),
        noPrice: num(r, 'NoPrice'),
      })),
    [catalogPricing.rows]
  );

  const pricingTotals = useMemo(() => {
    const t = pricing.reduce(
      (a, r) => ({
        managed: a.managed + r.managed,
        current: a.current + r.current,
        expired: a.expired + r.expired,
        noPrice: a.noPrice + r.noPrice,
      }),
      { managed: 0, current: 0, expired: 0, noPrice: 0 }
    );
    return {
      ...t,
      currentPct: t.managed ? (100 * t.current) / t.managed : 0,
      anyPricePct: t.managed ? (100 * (t.managed - t.noPrice)) / t.managed : 0,
    };
  }, [pricing]);

  // Part cost invested, managed vs unmanaged. NOTE: ~98% of units enter at $0 (teardown — capital
  // is booked to the airframe lot, not the part), so this sums only parts bought directly as parts.
  // The share, not the absolute dollars, is the meaningful figure. See dw-docs WHDim / InventoryFact.
  const cost = useMemo(() => {
    const r = costShare.rows[0];
    const managed = r ? num(r, 'ManagedCost') : 0;
    const total = r ? num(r, 'TotalCost') : 0;
    return { managed, total, pct: total ? (100 * managed) / total : 0 };
  }, [costShare.rows]);

  // --- Section 3: who we sell to (channel) ------------------------------------------------------
  const channelRows = useMemo(
    () =>
      channel.rows.map((r) => ({
        channel: field(r, 'Channel'),
        freshness: field(r, 'Freshness'),
        key: field(r, 'KeyCustomer'),
        revenue: num(r, 'Revenue'),
      })),
    [channel.rows]
  );

  const byChannel = useMemo(() => {
    const map = {};
    channelRows.forEach((r) => {
      map[r.channel] = (map[r.channel] || 0) + r.revenue;
    });
    return Object.entries(map)
      .map(([ch, revenue]) => ({ channel: ch, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [channelRows]);

  const channelTotals = useMemo(() => {
    const total = channelRows.reduce((s, r) => s + r.revenue, 0);
    const keyRev = channelRows.filter((r) => r.key === 'Key').reduce((s, r) => s + r.revenue, 0);
    const freshReseller = channelRows
      .filter((r) => r.channel === 'Reseller' && r.freshness === 'Fresh')
      .reduce((s, r) => s + r.revenue, 0);
    const endUser = channelRows
      .filter((r) => r.channel === 'End User / Operator')
      .reduce((s, r) => s + r.revenue, 0);
    return {
      total,
      keyPct: total ? (100 * keyRev) / total : 0,
      freshResellerPct: total ? (100 * freshReseller) / total : 0,
      freshReseller,
      endUserPct: total ? (100 * endUser) / total : 0,
    };
  }, [channelRows]);

  // --- Section 4: velocity / disposition --------------------------------------------------------
  const velocityRows = useMemo(
    () =>
      velocity.rows.map((r) => ({
        cond: field(r, 'ReportingCondition'),
        sortOrder: num(r, 'SortOrder'),
        lines: num(r, 'Lines'),
        revenue: num(r, 'Revenue'),
        marginPct: num(r, 'GrossMarginPct'),
        distinctParts: num(r, 'DistinctParts'),
      })),
    [velocity.rows]
  );

  const velByCond = useMemo(
    () => velocityRows.filter((r) => r.cond !== 'TOTAL').sort((a, b) => a.sortOrder - b.sortOrder),
    [velocityRows]
  );

  const velocityTotals = useMemo(() => {
    const total = velocityRows.find((r) => r.cond === 'TOTAL');
    const repairedLines = velByCond
      .filter((r) => r.cond === 'SV' || r.cond === 'OH')
      .reduce((s, r) => s + r.lines, 0);
    const totalLines = velByCond.reduce((s, r) => s + r.lines, 0);
    const catalogSize = pricingTotals.managed || popTotals.total;
    return {
      revenue: total?.revenue ?? velByCond.reduce((s, r) => s + r.revenue, 0),
      distinctParts: total?.distinctParts ?? 0,
      catalogSoldPct: catalogSize ? (100 * (total?.distinctParts ?? 0)) / catalogSize : 0,
      repairedPct: totalLines ? (100 * repairedLines) / totalLines : 0,
    };
  }, [velocityRows, velByCond, pricingTotals, popTotals]);

  // Condition share of managed-part sales revenue: AR (as-removed) vs SV/OH (serviceable/overhauled).
  const conditionMix = useMemo(() => {
    const rev = (cond) => velByCond.find((r) => r.cond === cond)?.revenue ?? 0;
    const total =
      velocityRows.find((r) => r.cond === 'TOTAL')?.revenue ??
      velByCond.reduce((s, r) => s + r.revenue, 0);
    const svoh = rev('SV') + rev('OH');
    return {
      arPct: total ? (100 * rev('AR')) / total : 0,
      svohPct: total ? (100 * svoh) / total : 0,
    };
  }, [velByCond, velocityRows]);

  // Stock readiness of the managed list: what we actually hold on the shelf per part.
  const readiness = useMemo(() => {
    const r = stockReadiness.rows[0];
    const total = r ? num(r, 'ManagedParts') : 0;
    const pct = (v) => (total ? (100 * v) / total : 0);
    const bucket = (label, key) => {
      const parts = r ? num(r, key) : 0;
      return { label, parts, pct: pct(parts), pctLabel: `${pct(parts).toFixed(1)}%` };
    };
    return {
      total,
      withAR: r ? num(r, 'PartsWithAR') : 0,
      withARPct: pct(r ? num(r, 'PartsWithAR') : 0),
      arQty: r ? num(r, 'ARQty') : 0,
      withSVOH: r ? num(r, 'PartsWithSVOH') : 0,
      withSVOHPct: pct(r ? num(r, 'PartsWithSVOH') : 0),
      svohQty: r ? num(r, 'SVOHQty') : 0,
      buckets: [
        bucket('Repaired < 1 yr', 'RepairedU1'),
        bucket('Repaired 1+ yr', 'Repaired1P'),
        bucket('Not repaired · AR in stock', 'NotRepAR'),
        bucket('Not repaired · no AR', 'NotRepNoAR'),
      ],
    };
  }, [stockReadiness.rows]);

  // --- colors -----------------------------------------------------------------------------------
  const sspColor = (name) =>
    ({
      [SSP_CURRENT]: c.positive,
      [SSP_EXPIRED]: c.negative,
      [SSP_NO_DATE]: c.series[2],
      [SSP_NONE]: c.series[3],
    }[name] ?? c.neutral);

  const classColor = (cls) =>
    ({ A: c.series[0], B: c.series[1], C: c.series[2], D: c.series[3], Z: c.series[4] }[cls] ??
      c.neutral);

  const reasonColor = (reason) => (reason.startsWith('Class A/B') ? c.series[0] : c.series[3]);

  const channelColor = (ch) =>
    ({
      Reseller: c.negative,
      'End User / Operator': c.positive,
      'Repair Station': c.series[2],
      'OEM / Tier 1': c.series[1],
    }[ch] ?? c.neutral);

  const readinessColor = (label) =>
    ({
      'Repaired < 1 yr': c.positive,
      'Repaired 1+ yr': c.series[2],
      'Not repaired · AR in stock': c.series[0],
      'Not repaired · no AR': c.neutral,
    }[label] ?? c.neutral);

  const condColor = (cond) =>
    ({ AR: c.series[3], SV: c.positive, OH: c.series[4], NEW: c.series[1] }[cond] ?? c.neutral);

  const notLoadedYet = !coverage.isLoading && !coverage.error && summary.managedLines === 0;

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        <Group gap="xs" wrap="wrap">
          <Text size="xs" c="dimmed">
            Reference:
          </Text>
          {MEMOS.map((m) => (
            <Button
              key={m.file}
              component="a"
              href={memoHref(m.file)}
              download
              size="xs"
              variant="subtle"
              leftSection={<IconFileDownload size={14} />}
            >
              {m.label}
            </Button>
          ))}
        </Group>

        {notLoadedYet && (
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={18} />} title="Waiting on the warehouse">
            No managed parts are showing yet. `PartDim.ManagedPart` is populated by the DW load —
            until a full load has run, every row reads as unmanaged. Nothing is wrong with the
            query; it just has no flagged parts to find.
          </Alert>
        )}

        {/* ============================ SECTION 1 — THE MANAGED POPULATION ======================= */}
        <SectionHeading
          title="1 · The managed population"
          subtitle="What we assigned. “Managed” is the inventory ITS deliberately prioritizes for our Key Target Market Segment Customers; the current list was seeded from Class A/B parts plus parts we've repaired and hold in stock."
        />

        {/* Seed / parity explainer hidden for now (kept to restore later). The memos are
            downloadable from the Reference links at the top of the page.
        <Alert color="yellow" variant="light" icon={<IconInfoCircle size={18} />} title="How the managed list was seeded — and where it's headed">
          ...two classifications, seed = Class A/B + repaired stock, Targeted vs Min/Max...
        </Alert>
        */}

        <QueryState isLoading={composition.isLoading} error={composition.error} isEmpty={!compositionData.length}>
          <SimpleGrid cols={{ base: 3, md: 3 }} spacing="md">
            <StatTile label="Managed parts" value={fmtInt(popTotals.total)} sub="On the list today" color={c.series[0]} asOf />
            <StatTile label="Class A/B core" value={fmtInt(popTotals.ab)} sub="Deliberate designation" color={c.positive} asOf />
            <StatTile label="C/D/Z tail" value={fmtInt(popTotals.tail)} sub="Seeded via repaired stock" color={c.series[3]} asOf />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Managed parts by class"
            caption="The managed population by ABC class. The A/B core is the deliberate designation; the C/D/Z tail was seeded from repaired stock on the shelf."
            asOf
          >
            <QueryState isLoading={composition.isLoading} error={composition.error} isEmpty={!compositionData.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={compositionData} dataKey="parts" nameKey="cls" innerRadius="50%" outerRadius="78%" paddingAngle={2}>
                    {compositionData.map((d) => (
                      <Cell key={d.cls} fill={classColor(d.cls)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={(v) => `${fmtInt(v)} parts`} />} wrapperStyle={{ outline: 'none' }} />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="How the managed list was seeded"
            caption="Which of the two seed rules put each part on the list (Class A/B takes priority when a part satisfies both). This is the launch seed, not an ongoing per-part rationale."
            asOf
          >
            <QueryState isLoading={composition.isLoading} error={composition.error} isEmpty={!reasonData.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={reasonData} dataKey="parts" nameKey="reason" innerRadius="50%" outerRadius="78%" paddingAngle={2}>
                    {reasonData.map((d) => (
                      <Cell key={d.reason} fill={reasonColor(d.reason)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={(v) => `${fmtInt(v)} parts`} />} wrapperStyle={{ outline: 'none' }} />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>

        {/* ============================ SECTION 2 — ARE WE PRICING THEM RIGHT? =================== */}
        <SectionHeading
          title="2 · Are we pricing them right?"
          subtitle="A managed part must carry a current SSP. Coverage across the managed catalog and the value of the inventory we hold."
        />

        <QueryState isLoading={catalogPricing.isLoading} error={catalogPricing.error} isEmpty={!pricing.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Managed parts with a current SSP"
              value={fmtPct(pricingTotals.currentPct)}
              sub={`${fmtInt(pricingTotals.current)} of ${fmtInt(pricingTotals.managed)} parts`}
              color={c.negative}
              asOf
            />
            <StatTile
              label="Expired SSP"
              value={fmtInt(pricingTotals.expired)}
              sub="Priced once, now stale"
              color={c.series[2]}
              asOf
            />
            <StatTile
              label="Cost invested in managed parts"
              value={fmtMoney(cost.managed)}
              sub="Booked part cost; most stock enters at $0 via teardown"
              color={c.series[0]}
              asOf
            />
            <StatTile
              label="Share of part cost in managed"
              value={fmtPct(cost.pct)}
              sub={`Managed vs ${fmtMoney(cost.total - cost.managed)} unmanaged`}
              color={c.positive}
              asOf
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Catalog SSP coverage by class"
            caption="Every managed part by class. Current means a set, non-expired SSP (Class A 90 days, B–D 180). The bulk sit in expired / never-dated / no-price — the pricing backlog."
            asOf
          >
            <QueryState isLoading={catalogPricing.isLoading} error={catalogPricing.error} isEmpty={!pricing.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pricing} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="cls" stroke={c.axis} fontSize={12} />
                  <YAxis stroke={c.axis} fontSize={12} width={56} />
                  <Tooltip content={<ChartTooltip formatter={(v) => `${fmtInt(v)} parts`} />} cursor={{ fill: c.grid, opacity: 0.3 }} wrapperStyle={{ outline: 'none' }} />
                  <Legend iconType="circle" />
                  <Bar dataKey="current" name="Current SSP" stackId="a" fill={c.positive} />
                  <Bar dataKey="expired" name="Expired" stackId="a" fill={c.negative} />
                  <Bar dataKey="setNoDate" name="Priced, no date" stackId="a" fill={c.series[2]} />
                  <Bar dataKey="noPrice" name="No price" stackId="a" fill={c.neutral} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Revenue by SSP status (managed parts)"
            caption="Of the revenue from managed parts sold, how much was on a current SSP at the time of sale. Expiry follows the same rule — Class A 90 days, B–D 180."
          >
            <QueryState isLoading={coverage.isLoading} error={coverage.error} isEmpty={!managedBySsp.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={managedBySsp} dataKey="revenue" nameKey="ssp" innerRadius="50%" outerRadius="78%" paddingAngle={2}>
                    {managedBySsp.map((r) => (
                      <Cell key={r.ssp} fill={sspColor(r.ssp)} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip formatter={fmtMoney} />} wrapperStyle={{ outline: 'none' }} />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>

        {/* ============================ SECTION 3 — WHO ARE WE SELLING TO? ======================= */}
        <SectionHeading
          title="3 · Who are we selling them to?"
          subtitle="Ryan's 7/21 memo's core sales rule: place managed material with key accounts and end users — not sell our best parts off to resellers."
        />

        <QueryState isLoading={channel.isLoading} error={channel.error} isEmpty={!channelRows.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Sold to key accounts"
              value={fmtPct(channelTotals.keyPct)}
              sub="Share of managed-part revenue (airframe/engine key)"
              color={c.positive}
            />
            <StatTile
              label="Sold to end users / operators"
              value={fmtPct(channelTotals.endUserPct)}
              sub="Share of managed-part revenue"
              color={c.series[0]}
            />
            <StatTile
              label="Sold in AR condition"
              value={fmtPct(conditionMix.arPct)}
              sub="As-removed · share of managed-part sales"
              color={c.series[3]}
            />
            <StatTile
              label="In SV or OH condition"
              value={fmtPct(conditionMix.svohPct)}
              sub="Serviceable / overhauled · share of managed-part sales"
              color={c.positive}
            />
          </SimpleGrid>
        </QueryState>

        <ChartCard
          title="Where managed parts go, by channel"
          caption="Revenue from managed parts, by customer channel. Reseller/broker sales are flagged — a managed part sold to a reseller is one we did not place with an end user or key account."
          height={260}
        >
          <QueryState isLoading={channel.isLoading} error={channel.error} isEmpty={!byChannel.length}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byChannel} layout="vertical" margin={{ top: 8, right: 16, left: 40, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} horizontal={false} />
                <XAxis type="number" stroke={c.axis} fontSize={12} tickFormatter={fmtMoney} />
                <YAxis type="category" dataKey="channel" stroke={c.axis} fontSize={12} width={120} />
                <Tooltip content={<ChartTooltip formatter={fmtMoney} />} cursor={{ fill: c.grid, opacity: 0.3 }} wrapperStyle={{ outline: 'none' }} />
                <Bar dataKey="revenue" name="Revenue from managed parts" radius={[0, 4, 4, 0]}>
                  {byChannel.map((d) => (
                    <Cell key={d.channel} fill={channelColor(d.channel)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </QueryState>
        </ChartCard>

        {/* ============================ SECTION 4 — WHAT ARE WE DOING WITH THEM? ================= */}
        <SectionHeading
          title="4 · What are we doing with them?"
          subtitle="Managed sales, and — of the managed list — what we actually hold on the shelf and how ready it is to serve."
        />

        <QueryState isLoading={stockReadiness.isLoading} error={stockReadiness.error} isEmpty={!readiness.total}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Managed sales in window"
              value={fmtMoney(velocityTotals.revenue)}
              sub={`${fmtPct(summary.managedShare)} of all sales revenue`}
              color={c.series[0]}
            />
            <StatTile
              label="Have an AR unit on shelf"
              value={fmtPct(readiness.withARPct)}
              sub={`${fmtInt(readiness.withAR)} parts · ${fmtInt(readiness.arQty)} units`}
              color={c.series[3]}
              asOf
            />
            <StatTile
              label="Have SV/OH stock on shelf"
              value={fmtPct(readiness.withSVOHPct)}
              sub={`${fmtInt(readiness.withSVOH)} parts · ${fmtInt(readiness.svohQty)} units`}
              color={c.positive}
              asOf
            />
            <StatTile
              label="Nothing on the shelf"
              value={fmtPct(readiness.buckets[3]?.pct ?? 0)}
              sub={`${fmtInt(readiness.buckets[3]?.parts ?? 0)} managed parts, no SV/OH or AR`}
              color={c.negative}
              asOf
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Managed stock readiness"
            caption="What we HOLD, per part: repaired (SV/OH) stock by repair recency, only an AR core, or nothing. Assumes qty ≥ 1; NEW out of scope. Repaired 1+ yr also includes SV/OH with no repair date."
            height={260}
            asOf
          >
            <QueryState isLoading={stockReadiness.isLoading} error={stockReadiness.error} isEmpty={!readiness.total}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={readiness.buckets}
                    dataKey="parts"
                    nameKey="label"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                  >
                    {readiness.buckets.map((d) => (
                      <Cell key={d.label} fill={readinessColor(d.label)} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => `${fmtInt(v)} parts`} />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Managed sales by condition"
            caption="What we SELL, by revenue: as-removed (AR) vs repaired (SV/OH) vs new. Pairs with what we hold — repaired conditions carry most of the revenue, AR is core material."
            height={260}
          >
            <QueryState isLoading={velocity.isLoading} error={velocity.error} isEmpty={!velByCond.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={velByCond}
                    dataKey="revenue"
                    nameKey="cond"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                  >
                    {velByCond.map((d) => (
                      <Cell key={d.cond} fill={condColor(d.cond)} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<ChartTooltip formatter={fmtMoney} />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>

        {/* ============================ The list ================================================= */}
        <SectionHeading
          title="The managed list"
          subtitle="Every managed part — searchable, filterable, exportable. The current list, as of today — not affected by the date selector."
        />
        <ManagedPartsTable />
      </Stack>
    </KpiPage>
  );
}
